/* eslint-disable no-unused-vars */
import React, { useContext, useState, useEffect, useRef } from 'react';
import useStyles from '../useStyles';
import { DialogContext } from '../../../contexts/DialogContext';
import { dialogActions } from '../../../reducers/dialogReducer';
import ControlledFormingElNoTableToolbar from './SettingsConfigControlledFormingElNoToolbar';
import ControlledFormingElNoConfigTable from './SettingConfigControlledFormingElNoTable';
import styleConst from '../../../constants/style';
import useCancelToken from '../../../hooks/useCancelToken';
import Typography from '../../../components/Typography/Typography';
import PaperContainer from '../../../components/PaperContainer/PaperContainer';
import TablePagination from '../../../components/TablePagination/TablePagination';
import Snackbar from '../../../components/Snackbar/Snackbar';
import {
  deleteControlledFormingElNo,
  getControlledFormingElNoList,
} from '../../../services/settings-config';

const SettingsConfigControlledFormingElNo = () => {
  const { classes } = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [controlledFormingElNoList, setControlledFormingElNoList] = useState([]);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [showStatus, setShowStatus] = useState(false);
  const [editedData, setEditedData] = useState({});
  const snackbarTimer = useRef(null);
  const { dialogDispatch } = useContext(DialogContext);
  const { newCancelToken } = useCancelToken();

  const rowsPerPage = styleConst.ROWS_PER_PAGE;

  const openSnackbar = () => {
    setShowStatus(true);
    clearTimeout(snackbarTimer.current);
    snackbarTimer.current = setTimeout(() => {
      setShowStatus(false);
      clearTimeout(snackbarTimer.current);
    }, 3000);
  };

  const getControlledFormingElNoConfig = async (
    page = currentPage,
    keyWord,
    isFromLastPage = false
  ) => {
    const data = keyWord
      ? { page: page - 1, keyWord, pageSize: rowsPerPage }
      : { page: page - 1, pageSize: rowsPerPage };
    const cancelToken = newCancelToken();
    await getControlledFormingElNoList({
      data,
      cancelToken,
      onSuccess: (result) => {
        const { count, elNoList } = result;
        setTotalRows(count);
        setControlledFormingElNoList(
          elNoList.map((item) => ({
            rowId: item.elNo,
            elNo: item.elNo,
            productName: item.productName,
            materialName: item.material,
          }))
        );
        setIsLoading(false);
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        if (isFromLastPage || currentPage <= 1) {
          dialogDispatch({
            type: dialogActions.OPEN,
            config: {
              title: '無法取得管制壓型規格設定',
              message:
                noPrivilegeMsg ||
                `目前無法取得管制壓型規格設定，請確認網路是否正常或聯繫軟體部門：${result}`,
            },
          });
          return;
        }
        setCurrentPage(currentPage - 1);
        getControlledFormingElNoConfig(currentPage - 1, '', true);
      },
    });
  };

  const onAddControlledFormingElNoSuccess = () => {
    getControlledFormingElNoConfig();
    setStatusMsg('新增管制壓型規格設定成功！');
    setStatusType('success');
    openSnackbar();
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    getControlledFormingElNoConfig(newPage);
  };

  const handleRowDelete = async (targetRow) => {
    const { elNo } = targetRow;
    const data = { elNo };
    const cancelToken = newCancelToken();
    await deleteControlledFormingElNo({
      data,
      cancelToken,
      onSuccess: () => {
        getControlledFormingElNoConfig();
        setStatusMsg(`已成功刪除 ${elNo} 管制壓型規格設定！`);
        setStatusType('success');
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        setStatusType('error');
        setStatusMsg(noPrivilegeMsg || result);
      },
      onSettled: () => {
        openSnackbar();
      },
    });
  };

  useEffect(() => {
    setCurrentPage(1);
    getControlledFormingElNoConfig();

    return () => {
      clearTimeout(snackbarTimer.current);
    };
  }, []);

  return (
    <PaperContainer className={classes.container}>
      <Typography variant="h6">管制壓型規格設定</Typography>
      <ControlledFormingElNoTableToolbar
        onAddControlledFormingElNoSuccess={onAddControlledFormingElNoSuccess}
      />
      <Snackbar msg={statusMsg} type={statusType} open={showStatus} className={classes.snackbar} />
      <ControlledFormingElNoConfigTable
        isLoading={isLoading}
        tableContentList={controlledFormingElNoList}
        editedData={editedData}
        onRowDelete={handleRowDelete}
      />
      <TablePagination
        isEditing={false}
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onChangePage={handleChangePage}
      />
    </PaperContainer>
  );
};

export default SettingsConfigControlledFormingElNo;
